// frontend/src/components/CreateCodeModal.jsx
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import PropTypes from "prop-types";
import { createCode } from "../api/codes";
import { moveCodeToFolder } from "../api/folders";
import { addTagToCode } from "../api/tags";
import QrPayloadFields, {
  QR_TYPES,
  buildPayloadField,
  INPUT_CLS,
  LABEL_CLS,
} from "./QrPayloadFields";
import CodeAdvancedFields, {
  buildPlatformDestinations,
  localToIso,
} from "./CodeAdvancedFields";

const EMPTY_ADVANCED = {
  folderId: "",
  tagIds: [],
  expiresAt: "",
  activeFrom: "",
  platformEnabled: false,
  iosUrl: "",
  androidUrl: "",
  defaultUrl: "",
};

/**
 * Build the POST /api/codes body for the selected QR type.
 *
 * @param {object} v    Raw react-hook-form values
 * @param {object} adv  Advanced-fields state (expiry / scheduling / platform)
 * @returns {object}  `{ name?, qr_type, payload|content_json, expires_at?, ... }`
 */
function buildCreateBody(v, adv) {
  const qrType = v.qr_type || "url";
  const body = {
    name: v.name || undefined,
    qr_type: qrType,
    ...buildPayloadField(qrType, v),
  };
  const expiresAt = localToIso(adv.expiresAt);
  const activeFrom = localToIso(adv.activeFrom);
  const platform = buildPlatformDestinations(adv);
  if (expiresAt) body.expires_at = expiresAt;
  if (activeFrom) body.active_from = activeFrom;
  if (platform) body.platform_destinations = platform;
  return body;
}

/**
 * CreateCodeModal — slide-in modal for creating a new QR code.
 *
 * After a successful creation the modal transitions to a "success" view
 * displaying the auto-generated token in a read-only field so the user
 * can copy it before closing.
 *
 * @param {object}   props
 * @param {Function} props.onClose    Called to close the modal without changes
 * @param {Function} props.onCreated  Called (with no args) when Done is clicked
 */
function CreateCodeModal({ onClose, onCreated, folders, tags }) {
  const [submitting, setSubmitting] = useState(false);
  const [createdCode, setCreatedCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [advanced, setAdvanced] = useState(EMPTY_ADVANCED);

  const mergeAdvanced = (partial) =>
    setAdvanced((prev) => ({ ...prev, ...partial }));

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    shouldUnregister: true,
    // "address" is location's default mode — set here (rather than a radio
    // defaultChecked, which would fight react-hook-form's own defaultValues
    // handling once the edit form's payloadToDefaults sets "coords" instead).
    defaultValues: { location_mode: "address" },
  });

  // The selected QR type drives which payload fields are shown + submitted.
  const qrType = watch("qr_type", "url");

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      const created = await createCode(buildCreateBody(values, advanced));
      // Folder + tags attach through their own endpoints after creation.
      // A failure here is non-fatal: the code exists, so warn rather than abort.
      try {
        if (advanced.folderId) {
          await moveCodeToFolder(created.id, advanced.folderId);
        }
        for (const tagId of advanced.tagIds) {
          await addTagToCode(created.id, tagId);
        }
      } catch {
        toast.error("Code created, but applying folder/tags failed.");
      }
      setCreatedCode(created);
    } catch {
      toast.error("Failed to create code. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!createdCode) return;
    try {
      await navigator.clipboard.writeText(createdCode.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy to clipboard.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={createdCode ? undefined : onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {createdCode ? (
          /* ── Success view ─────────────────────────────────────────────── */
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-green-500">
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </span>
              <h2 className="text-base font-semibold text-gray-900">
                Code created!
              </h2>
            </div>

            {createdCode.name && (
              <p className="text-sm text-gray-600">
                <span className="font-medium">Label:</span> {createdCode.name}
              </p>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your scan token
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={createdCode.code}
                  className="flex-1 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm font-mono text-gray-800 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                >
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-400">
                This token is encoded into the QR image and cannot be changed.
              </p>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={onCreated}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          /* ── Creation form ────────────────────────────────────────────── */
          <>
            <h2 className="text-base font-semibold text-gray-900 mb-5">
              New QR Code
            </h2>

            <form
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              className="space-y-4"
            >
              {/* Optional label */}
              <div>
                <label className={LABEL_CLS} htmlFor="create-name">
                  Label{" "}
                  <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  id="create-name"
                  type="text"
                  placeholder="e.g. Q4 Promo"
                  className={INPUT_CLS}
                  {...register("name", {
                    maxLength: { value: 200, message: "Max 200 characters." },
                  })}
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.name.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-400">
                  The scan token is auto-generated — no need to choose one.
                </p>
              </div>

              {/* QR type selector */}
              <div>
                <div className="flex items-center justify-between">
                  <label className={LABEL_CLS} htmlFor="create-qr-type">
                    QR type
                  </label>
                  <a
                    href="/docs/faq#what-qr-code-types-are-supported"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mb-1 text-xs text-blue-600 hover:underline"
                  >
                    What are these? ↗
                  </a>
                </div>
                <select
                  id="create-qr-type"
                  className={INPUT_CLS}
                  defaultValue="url"
                  {...register("qr_type")}
                >
                  {QR_TYPES.filter((t) => !t.group).map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                  <optgroup label="More types">
                    {QR_TYPES.filter((t) => t.group).map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>

              {/* Type-specific payload fields */}
              <QrPayloadFields
                qrType={qrType}
                register={register}
                errors={errors}
                idPrefix="create"
                control={control}
                watch={watch}
              />

              {/* Folder / tags / expiry / scheduling / platform */}
              <CodeAdvancedFields
                folders={folders}
                tags={tags}
                value={advanced}
                onChange={mergeAdvanced}
              />

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? "Creating…" : "Create"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

CreateCodeModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
  folders: PropTypes.array,
  tags: PropTypes.array,
};
CreateCodeModal.defaultProps = { folders: [], tags: [] };

export default CreateCodeModal;
